import BaseService from "./BaseService";
import { toast, Notification } from "components/ui";


const ERROR_TOAST_DURATION = 5000;
const activeErrorToasts = new Set();

const pushErrorToast = (title, message) => {
  if (activeErrorToasts.has(message)) {
    return;
  }
  activeErrorToasts.add(message);
  toast.push(
    <Notification title={title} type="danger" duration={ERROR_TOAST_DURATION}>
      {message}
    </Notification>,
    {
      placement: "top-center",
    }
  );
  setTimeout(() => activeErrorToasts.delete(message), ERROR_TOAST_DURATION);
};


const STATIC_REFERENCE_GETS = new Set([
  // Geography + statutory GST/tax reference (never company-configured):
  "Master/GetStates", "Master/GetDistricts", "Master/GetGSTRates", "Master/GetGSTTreatment",
  "Master/GetGstTreatment", "Master/GetGSTTypes", "Master/GetInterGSTRates", "Master/GetIntraGSTRates",
  "Master/GetTaxPrefrences", "Master/GetAllTdsTax", "Master/GetGoodsUnit", "Master/GetFrequencies",
  "Master/GetAllDeliveryChallanType", "Master/GetServiceTypes", "Master/GetAllInvoiceStatus",
  // Universal demographic lists (not per-company):
  "Master/GetAllReligion", "Master/GetAllBloodGroup", "Master/GetAllMarital", "Master/GetAllNationality",
  "Master/GetAllCountryOrigin", "Master/GetAllRelation", "Master/GetAllDisability", "Master/GetAllResidential",
  // NOTE: HR/admin lookups that could be company-configured (Designation, Department, Grade,
  // Document, LeaveType, CreditReasons, …) are deliberately NOT cached so newly-added values
  // are never stale mid-session.
]);
const _referenceCache = new Map();

const staticCacheKey = (param) => {
  const method = (param?.method || "get").toLowerCase();
  if (method !== "get") return null;
  const rawUrl = param?.url;
  if (typeof rawUrl !== "string") return null;
  const path = rawUrl.split("?")[0];
  if (!STATIC_REFERENCE_GETS.has(path)) return null;
  // Key by full url + params so e.g. GetDistricts?StateId=5 vs 6 stay distinct.
  return rawUrl + "::" + JSON.stringify(param?.params ?? null);
};

// Clear cached reference data (call on login/logout, or if an admin edits a lookup).
export const clearReferenceCache = () => _referenceCache.clear();

const ApiService = {
  fetchData(param) {
    const cacheKey = staticCacheKey(param);
    if (cacheKey && _referenceCache.has(cacheKey)) {
      return _referenceCache.get(cacheKey);
    }
    const resultPromise = new Promise((resolve, reject) => {
      BaseService(param)
        .then((response) => {
          resolve(response);

          
          const method = (param?.method || "get").toLowerCase();
          const isMutation = ["post", "put", "patch", "delete"].includes(method);

      
          const bodyStatus = String(response?.data?.status ?? "").toLowerCase();
          const isFailureStatus =
            bodyStatus === "error" || bodyStatus === "fail" || bodyStatus === "failed";

        
          const isDelete = method === "delete" || /delete/i.test(param?.url || "");
          const notifyType = isDelete ? "danger" : "success";
          const notifyTitle = isDelete ? "Deleted" : "Successfully Done";

          // A delete toasts even when the response carries no message. Every
          // delete endpoint returns a bare body -- `Ok()`, `Ok(true)`,
          // `Ok(new { deleted })` -- so requiring a message meant the red
          // "Deleted" toast above never fired, and the only thing left on screen
          // was whatever green success toast the page raised for itself. The
          // other verbs keep the message requirement so mutations that are
          // deliberately silent stay silent.
          if (
            isMutation &&
            !param?.skipGlobalSuccessToast &&
            (isDelete || response?.data?.message || response?.data?.response) &&
            response?.data?.succeeded !== false &&
            !isFailureStatus
          ) {
            const message =
              typeof response?.data?.message === "string"
                ? response.data.message
                : typeof response?.data?.response === "string"
                ? response.data.response
                : "";

            const isSingleService = message
              .toLowerCase()
              .startsWith("single service") || message.toLowerCase().startsWith("service item added")

            if (!isSingleService) {
              toast.push(
                <Notification
                  title={notifyTitle}
                  type={notifyType}
                  duration={5000}
                >
                  {message ||
                    (isDelete
                      ? "Deleted successfully"
                      : "Operation completed successfully")}
                </Notification>,
                {
                  placement: "top-center",
                }
              );
            }
          }
        })
        .catch((errors) => {
     
          if (param?.rawError) {
            return reject(errors);
          }

          const errorResponse = errors.response?.data;

          if (errorResponse) {
 
            const stringBody =
              typeof errorResponse === "string" ? errorResponse : null;

            const errorTitle = errorResponse.title || "Validation Error";

            // Extract the validation error details
            const validationErrors = errorResponse.errors;
            const errorMessages = validationErrors
              ? Object.entries(validationErrors)
                  .map(([, messages]) =>
                    Array.isArray(messages) ? messages.join(", ") : String(messages)
                  )
                  .join(" | ")
              : stringBody || errorResponse.message || "An unexpected error occurred.";

            
            if (!param?.skipGlobalErrorToast) {
              pushErrorToast(errorTitle, errorMessages);
            }
          } else {
            // Default error message for unexpected issues
            if (!param?.skipGlobalErrorToast) {
              pushErrorToast("Error", "An unexpected error occurred.");
            }
          }

 
          const rejectionMessage =
            (typeof errorResponse === "string" && errorResponse) ||
            errorResponse?.message ||
            errorResponse?.title ||
            "An unexpected error occurred.";
          reject(rejectionMessage);
        });
    });
    if (cacheKey) {
      _referenceCache.set(cacheKey, resultPromise);
      resultPromise.catch(() => _referenceCache.delete(cacheKey));
    }
    return resultPromise;
  },
};

export default ApiService;
